import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { Button as BaseButton } from 'qampo-ui-library/base';
import styles from './Button.module.css';

type BaseButtonProps = ComponentProps<typeof BaseButton>;

// Handlers may return a promise (see handleClick); `unknown` instead of
// `void | Promise<unknown>` because void is not valid in a union type.
type ButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => unknown;

interface ButtonProps extends Omit<BaseButtonProps, 'onClick'> {
  onClick?: ButtonClickHandler;
}

export const Button = ({
  classes,
  size = 'regular',
  type,
  disabled,
  onClick,
  ...rest
}: ButtonProps) => {
  const [isPending, setIsPending] = useState(false);
  const isMountedRef = useRef(true);

  // Reset on each mount, not just initial render — under React StrictMode the
  // effect runs cleanup→mount in dev, and without this assignment the ref would
  // stay `false` after the first cycle, permanently sticking isPending=true.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // If onClick returns a Promise, auto-disable the button until it settles so
  // callers don't have to wire their own `saving`/`deleting` state. Sync handlers
  // are unaffected. Rejections are consumed here (via the second .then arg) so
  // they don't become unhandled — the contract is that handlers surface their
  // own errors; Button only manages pending state. The isMountedRef guard
  // prevents setState if the component unmounts mid-request (e.g. drawer closes).
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      if (isPending || !onClick) return;
      const result = onClick(e);
      if (result instanceof Promise) {
        setIsPending(true);
        const clearPending = (): void => {
          if (isMountedRef.current) setIsPending(false);
        };
        void result.then(clearPending, clearPending);
      }
    },
    [onClick, isPending]
  );

  const mergedClasses = [
    styles.root,
    size === 'regular' && styles.regular,
    styles[type],
    classes
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <BaseButton
      {...rest}
      type={type}
      size={size}
      onClick={handleClick}
      disabled={disabled || isPending}
      classes={mergedClasses}
    />
  );
};
